import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './OrderTracking.css';

const OrderTracking = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [showCancelPopup, setShowCancelPopup] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);

    const fetchOrder = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/orders/${orderId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const cleanedOrder = {
          orderId: response.data.orderId || 'N/A',
          status: response.data.status || 'Processing',
          totalPrice: response.data.totalPrice || 0,
          createdAt: response.data.createdAt ? new Date(response.data.createdAt).toLocaleString() : 'N/A',
          updatedAt: response.data.updatedAt ? new Date(response.data.updatedAt).toLocaleString() : 'N/A',
          statusHistory: response.data.statusHistory || [{ status: 'Processing', timestamp: response.data.createdAt }],
          deliveryDetails: {
            name: response.data.deliveryDetails?.name || '',
            mobile: response.data.deliveryDetails?.mobile || '',
            address: response.data.deliveryDetails?.address || '',
            city: response.data.deliveryDetails?.city || '',
            state: response.data.deliveryDetails?.state || '',
            pincode: response.data.deliveryDetails?.pincode || '',
          },
          items: (response.data.items || []).map(item => ({
            productId: item.productId || '',
            name: item.name || '',
            price: item.price || 0,
            quantity: item.quantity || 1,
            weight: item.weight || 'N/A',
            image: item.image || '/placeholder-image.png',
          })),
        };

        setOrder(cleanedOrder);

        try {
          await axios.put(
            `${process.env.REACT_APP_API_BASE_URL}/api/orders/${orderId}/viewed`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );
        } catch (viewError) {
          console.error('Error marking order as viewed:', viewError);
          setError('Failed to mark order as viewed.');
        }
      } catch (error) {
        console.error('Error fetching order:', error);
        setError('Failed to fetch order. Please try again.');
        setOrder(null);
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [orderId, navigate]);

  const getTimelineSteps = (status, statusHistory, createdAt) => {
    const getLatestStatusTimestamp = (status) => {
      const entries = statusHistory.filter(h => h.status === status);
      if (entries.length > 0) {
        const latest = entries.reduce((latest, current) =>
          new Date(current.timestamp) > new Date(latest.timestamp) ? current : latest
        );
        return new Date(latest.timestamp).toLocaleString();
      }
      return '';
    };

    const steps = [
      {
        status: 'Order Placed',
        completed: true,
        date: createdAt,
      },
      {
        status: 'Confirmed',
        completed: ['confirmed', 'shipped', 'delivered'].includes(status),
        date: getLatestStatusTimestamp('confirmed'),
      },
      {
        status: 'Shipped',
        completed: ['shipped', 'delivered'].includes(status),
        date: getLatestStatusTimestamp('shipped'),
      },
      {
        status: 'Delivered',
        completed: status === 'delivered',
        date: getLatestStatusTimestamp('delivered'),
      },
    ];

    if (status === 'cancelled') {
      const cancelDate = getLatestStatusTimestamp('cancelled') || new Date().toLocaleString();
      return steps.map((step, index) => ({
        ...step,
        completed: index === 0,
        cancelled: true,
        date: index === 0 ? createdAt : cancelDate,
      }));
    }

    return steps;
  };

  const handleCancelOrder = () => {
    if (order.status === 'cancelled' || order.status === 'delivered') return;
    setShowCancelPopup(true);
  };

  const confirmCancelOrder = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    setLoading(true);
    try {
      await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/orders/${orderId}/cancel`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setOrder({
        ...order,
        status: 'cancelled',
        statusHistory: [...order.statusHistory, { status: 'cancelled', timestamp: new Date() }],
      });
      setShowCancelPopup(false);
    } catch (error) {
      console.error('Error cancelling order:', error);
      setError('Failed to cancel order. Please try again.');
      setShowCancelPopup(false);
    } finally {
      setLoading(false);
    }
  };

  const closePopup = () => setShowCancelPopup(false);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '60vh' }}>
        <div className="spinner-border text-success" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="ot-container">
        <h1 className="ot-error-heading">Order Not Found</h1>
        {error && <div className="alert alert-danger">{error}</div>}
        <button className="ot-action-btn ot-orders-btn" onClick={() => navigate('/my-orders')}>
          <i className="bi bi-bag"></i> View My Orders
        </button>
      </div>
    );
  }

  const timelineSteps = getTimelineSteps(order.status, order.statusHistory, order.createdAt);

  return (
    <div className="ot-container">
      {error && <div className="alert alert-danger">{error}</div>}
      <div className="ot-section">
        <h2 className="ot-section-heading">Order Summary</h2>
        <div className="ot-cart-items">
          {order.items.map((item) => (
            <div key={`${item.productId}-${item.weight}`} className="ot-cart-item">
              <img
                src={item.image}
                alt={item.name}
                className="ot-item-image"
                onError={(e) => { e.target.src = '/placeholder-image.png'; }}
              />
              <div className="ot-item-details">
                <p className="ot-item-name">{item.name}</p>
                <p className="ot-item-quantity">Qty: {item.quantity}, Weight: {item.weight}</p>
                <p className="ot-item-price">₹{(item.price * item.quantity).toFixed(2)}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="ot-order-total">
          <span>Total Price:</span>
          <span className="ot-total-amount">₹{order.totalPrice.toFixed(2)}</span>
        </div>
      </div>

      <div className="ot-section">
        <h2 className="ot-section-heading">Order Details</h2>
        <div className="ot-order-details">
          <p><strong>Delivery Address:</strong> {order.deliveryDetails.name}, {order.deliveryDetails.address}, {order.deliveryDetails.city}, {order.deliveryDetails.state} - {order.deliveryDetails.pincode}</p>
          <p><strong>Mobile:</strong> {order.deliveryDetails.mobile}</p>
          <p><strong>Order Date:</strong> {order.createdAt}</p>
          <p><strong>Last Updated:</strong> {order.updatedAt}</p>
        </div>
      </div>

      <div className="ot-section">
        <h2 className="ot-section-heading">Order Timeline</h2>
        <div className="ot-timeline">
          {timelineSteps.map((step, index) => (
            <div key={step.status} className="ot-timeline-step">
              <div className={`ot-timeline-icon ${step.cancelled ? 'ot-canceled' : step.completed ? 'ot-completed' : ''}`}>
                <i className={`bi ${step.completed || step.cancelled ? 'bi-check' : 'bi-circle'}`}></i>
              </div>
              <div className="ot-timeline-content">
                <p className="ot-timeline-status">{step.cancelled && index > 0 ? 'Cancelled' : step.status}</p>
                <p className="ot-timeline-date">{step.date}</p>
              </div>
              {index < timelineSteps.length - 1 && (
                <div className={`ot-timeline-connector ${step.cancelled ? 'ot-canceled' : step.completed ? 'ot-completed' : ''}`}></div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="ot-action-buttons">
        <button className="ot-action-btn ot-orders-btn" onClick={() => navigate('/my-orders')}>
          View My Orders
        </button>
        <button
          className={`ot-action-btn ot-cancel-btn ${order.status === 'cancelled' || order.status === 'delivered' ? 'ot-disabled' : ''}`}
          onClick={handleCancelOrder}
          disabled={order.status === 'cancelled' || order.status === 'delivered' || loading}
        >
          Cancel Order
        </button>
      </div>

      {showCancelPopup && (
        <div className="ot-popup-overlay">
          <div className="ot-popup">
            <h2 className="ot-popup-heading">Cancel Order</h2>
            <p className="ot-popup-message">Are you sure you want to cancel this order?</p>
            <div className="ot-popup-buttons">
              <button className="ot-popup-btn ot-popup-yes-btn" onClick={confirmCancelOrder} disabled={loading}>Yes</button>
              <button className="ot-popup-btn ot-popup-no-btn" onClick={closePopup}>No</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderTracking;